import React, { useEffect, useId, useRef, useState } from "react";
import styled from "styled-components";
import mermaid from "mermaid";

let mermaidInitialized = false;

const Figure = styled.div`
  margin: 18px 0;
  padding: 18px;
  border-radius: 18px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  overflow-x: auto;

  svg {
    display: block;
    max-width: 100%;
    height: auto;
    margin: 0 auto;
  }

  @media (max-width: 767px) {
    padding: 14px;
  }
`;

const ErrorBox = styled.div`
  margin: 18px 0;
  padding: 16px 18px;
  border-radius: 18px;
  border: 1px solid rgba(239, 68, 68, 0.35);
  background: rgba(239, 68, 68, 0.08);
  color: #b91c1c;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.6;

  pre {
    margin-top: 12px;
    padding: 14px;
    border-radius: 12px;
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.surfaceMuted};
    border: 1px solid ${({ theme }) => theme.colors.border};
    font-size: 12px;
    font-weight: 400;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
`;

type MermaidDiagramProps = {
  source: string;
};

const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ source }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const renderId = useId().replace(/:/g, "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const trimmed = source.trim();
    if (!trimmed || !containerRef.current) {
      return;
    }

    let cancelled = false;

    const renderDiagram = async () => {
      try {
        if (!mermaidInitialized) {
          mermaid.initialize({
            startOnLoad: false,
            securityLevel: "strict",
            theme: "neutral",
          });
          mermaidInitialized = true;
        }

        const { svg } = await mermaid.render(`mermaid-${renderId}`, trimmed);
        if (cancelled || !containerRef.current) {
          return;
        }

        containerRef.current.innerHTML = svg;
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Mermaid 다이어그램 렌더링에 실패했습니다.",
          );
        }
      }
    };

    void renderDiagram();

    return () => {
      cancelled = true;
    };
  }, [source, renderId]);

  if (error) {
    return (
      <ErrorBox role="alert">
        <p>다이어그램을 표시할 수 없습니다: {error}</p>
        <pre>{source}</pre>
      </ErrorBox>
    );
  }

  return <Figure ref={containerRef} aria-label="Mermaid diagram" />;
};

export default MermaidDiagram;
